import React, { useMemo } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

// =============================================================================
// Input shapes — same structure works for live entities and snapshot data
// =============================================================================

export interface SharesWorkAuthorPublisher {
  // Snapshot data may not include id (snapshot trees freeze fields, not IDs).
  // taxId / name are used as fallback dedupe keys.
  publisher: {
    id?: string;
    displayId?: string;
    name?: string;
    pseudonym?: string | null;
    taxId?: string;
  };
  percentage: number;
}

export interface SharesWorkAuthor {
  author: {
    id?: string;
    firstName?: string;
    lastName?: string;
    displayId?: string;
    taxId?: string;
  };
  lyricsPercentage: number;
  musicPercentage: number;
  publishers?: SharesWorkAuthorPublisher[];
}

export interface SharesWork {
  id: string;
  authors: SharesWorkAuthor[];
}

// CRUD shape (in-flight edit)
export interface SharesCrudWorkAuthorPublisher {
  id: string;
  showPublisher: {
    id: string;
    displayId?: string;
    name?: string;
    pseudonym?: string | null;
  } | null;
  percentage: number;
  __status__?: string;
}

export interface SharesCrudAuthor {
  id: string;
  showAuthor: { id: string; displayId?: string } | null;
  lyricsPercentage: number;
  musicPercentage: number;
  showPublishers?: SharesCrudWorkAuthorPublisher[];
  __status__?: string;
}

// =============================================================================
// Output — pure data that the chart consumes
// =============================================================================

export interface ShareSlice {
  id: string;
  name: string;
  /** 0..1 fraction (averaged across works if input had multiple) */
  share: number;
  type: "author" | "publisher" | "unassigned";
  /** For author slices: per-publisher breakdown shown in tooltip */
  publishers?: { name: string; percentage: number }[];
}

// =============================================================================
// Pure helpers — derive ShareSlice[] from each input shape
// =============================================================================

/**
 * Snapshot data freezes fields but not entity IDs, so we can't dedupe by
 * id alone. Fall back to the canonical legal identifier (taxId for both
 * authors and publishers) and finally to a name-derived key. Live data
 * still hits the first branch because its entities carry real ids.
 */
function stableKey(
  id: string | undefined,
  taxId: string | undefined,
  name: string
): string {
  if (id && id.trim()) return `id:${id}`;
  if (taxId && taxId.trim()) return `tax:${taxId}`;
  return `name:${name}`;
}

/**
 * Nombre visible de una editorial: la regla de `publisherDisplayId`
 * (PublisherCommonStore) y del server (`publisherDisplayName`) — el seudónimo
 * recortado o, si no hay, la razón social (`name`). Primero el `displayId`, si
 * vino calculado. No se reusa `publisherDisplayId` porque pide el LangStore y
 * este cálculo es puro. Hace falta porque hay fuentes con `pseudonym` y sin
 * `displayId`: la consulta de la vista de la obra, el snapshot de la edición
 * firmada y, una vez regenerada, la de la edición, que corta la profundidad
 * antes de la editorial. Antes caían directo a `name` y la torta mostraba la
 * razón social (KAN-440).
 */
function publisherDisplayName(publisher: {
  displayId?: string;
  name?: string;
  pseudonym?: string | null;
}): string {
  return (
    publisher.displayId ||
    publisher.pseudonym?.trim() ||
    publisher.name ||
    "Editorial"
  );
}

export function computeSharesFromWorks(works: SharesWork[]): ShareSlice[] {
  if (!works || works.length === 0) return [];
  const authorMap = new Map<
    string,
    {
      name: string;
      totalShare: number;
      publishers: { name: string; percentage: number }[];
    }
  >();
  const publisherMap = new Map<string, { name: string; totalShare: number }>();

  works.forEach((work) => {
    work.authors?.forEach((wa) => {
      if (!wa.author) return;
      const lyrics = Number(wa.lyricsPercentage) || 0;
      const music = Number(wa.musicPercentage) || 0;
      const authorTotalShare = (lyrics + music) / 100;
      if (authorTotalShare <= 0) return;

      const publishers = wa.publishers || [];
      const authorPublishersList: { name: string; percentage: number }[] = [];
      let publishersTotalPercentage = 0;

      publishers.forEach((pub) => {
        if (!pub.publisher) return;
        const pubPercentage = Number(pub.percentage) || 0;
        publishersTotalPercentage += pubPercentage;
        const publisherName = publisherDisplayName(pub.publisher);
        const publisherId = stableKey(
          pub.publisher.id,
          pub.publisher.taxId,
          publisherName
        );
        const pubShare = authorTotalShare * (pubPercentage / 100);

        authorPublishersList.push({
          name: publisherName,
          percentage: pubPercentage,
        });

        const existing = publisherMap.get(publisherId);
        if (existing) {
          existing.totalShare += pubShare;
        } else {
          publisherMap.set(publisherId, {
            name: publisherName,
            totalShare: pubShare,
          });
        }
      });

      const authorRemainingShare =
        authorTotalShare * (1 - publishersTotalPercentage / 100);
      if (authorRemainingShare <= 0) return;
      const authorName =
        wa.author.displayId ||
        `${wa.author.firstName ?? ""} ${wa.author.lastName ?? ""}`.trim() ||
        "Autor";
      const authorId = stableKey(
        wa.author.id,
        wa.author.taxId,
        authorName
      );

      const existing = authorMap.get(authorId);
      if (existing) {
        existing.totalShare += authorRemainingShare;
      } else {
        authorMap.set(authorId, {
          name: authorName,
          totalShare: authorRemainingShare,
          publishers: authorPublishersList,
        });
      }
    });
  });

  return buildSlices(authorMap, publisherMap, works.length);
}

export function computeSharesFromCrud(
  authors: SharesCrudAuthor[]
): ShareSlice[] {
  if (!authors || authors.length === 0) return [];
  const activeAuthors = authors.filter(
    (a) =>
      a.__status__ !== "deleted" &&
      a.__status__ !== "original-deleted" &&
      a.showAuthor
  );

  const authorMap = new Map<
    string,
    {
      name: string;
      totalShare: number;
      publishers: { name: string; percentage: number }[];
    }
  >();
  const publisherMap = new Map<string, { name: string; totalShare: number }>();

  activeAuthors.forEach((a) => {
    if (!a.showAuthor) return;
    const lyrics = Number(a.lyricsPercentage) || 0;
    const music = Number(a.musicPercentage) || 0;
    const authorTotalShare = (lyrics + music) / 100;
    if (authorTotalShare <= 0) return;

    const publishers = (a.showPublishers || []).filter(
      (p) =>
        !p.__status__ ||
        (p.__status__ !== "deleted" && p.__status__ !== "original-deleted")
    );
    const authorPublishersList: { name: string; percentage: number }[] = [];
    let publishersTotalPercentage = 0;

    publishers.forEach((pub) => {
      const pubPercentage = Number(pub.percentage) || 0;
      publishersTotalPercentage += pubPercentage;
      const publisherId = pub.showPublisher?.id || pub.id;
      const publisherName = pub.showPublisher
        ? publisherDisplayName(pub.showPublisher)
        : "Editorial";
      const pubShare = authorTotalShare * (pubPercentage / 100);

      authorPublishersList.push({
        name: publisherName,
        percentage: pubPercentage,
      });

      if (!publisherId) return;
      const existing = publisherMap.get(publisherId);
      if (existing) {
        existing.totalShare += pubShare;
      } else {
        publisherMap.set(publisherId, {
          name: publisherName,
          totalShare: pubShare,
        });
      }
    });

    const authorRemainingShare =
      authorTotalShare * (1 - publishersTotalPercentage / 100);
    if (authorRemainingShare <= 0) return;
    const authorId = a.showAuthor.id;
    const authorName = a.showAuthor.displayId || "Autor";

    const existing = authorMap.get(authorId);
    if (existing) {
      existing.totalShare += authorRemainingShare;
    } else {
      authorMap.set(authorId, {
        name: authorName,
        totalShare: authorRemainingShare,
        publishers: authorPublishersList,
      });
    }
  });

  return buildSlices(authorMap, publisherMap, 1);
}

function buildSlices(
  authorMap: Map<
    string,
    {
      name: string;
      totalShare: number;
      publishers: { name: string; percentage: number }[];
    }
  >,
  publisherMap: Map<string, { name: string; totalShare: number }>,
  worksCount: number
): ShareSlice[] {
  const result: ShareSlice[] = [];
  const round4 = (n: number) => Math.round(n * 10000) / 10000;

  authorMap.forEach((data, id) => {
    result.push({
      id,
      name: data.name,
      share: round4(data.totalShare / worksCount),
      type: "author",
      publishers: data.publishers,
    });
  });
  publisherMap.forEach((data, id) => {
    result.push({
      id,
      name: data.name,
      share: round4(data.totalShare / worksCount),
      type: "publisher",
    });
  });

  const totalAssigned = result.reduce((sum, e) => sum + e.share, 0);
  const unassigned = round4(1 - totalAssigned);
  if (unassigned > 0.001) {
    result.push({
      id: "unassigned",
      name: "Sin asignar",
      share: unassigned,
      type: "unassigned",
    });
  }

  return result.filter((s) => s.share > 0).sort((a, b) => b.share - a.share);
}

// =============================================================================
// Visual constants
// =============================================================================

const AUTHOR_COLORS = [
  "#1565C0",
  "#1976D2",
  "#1E88E5",
  "#2196F3",
  "#42A5F5",
  "#64B5F6",
  "#90CAF9",
  "#BBDEFB",
];

const PUBLISHER_COLORS = [
  "#E65100",
  "#F57C00",
  "#FF9800",
  "#FFB74D",
  "#D84315",
  "#FF5722",
  "#FF8A65",
  "#FFAB91",
];

const UNASSIGNED_COLOR = "#E0E0E0";

// =============================================================================
// Pure presentational component
// =============================================================================

export interface SharesPieChartProps {
  data: ShareSlice[];
  title?: string;
  subtitle?: string;
  /** Label shown in tooltip for the share value. Defaults to "Participación". */
  valueLabel?: string;
}

export const SharesPieChart: React.FC<SharesPieChartProps> = ({
  data,
  title,
  subtitle,
  valueLabel = "Participación",
}) => {
  // Recharts pinta la leyenda con un `<span>` propio, fuera del árbol de MUI:
  // no hereda `color` del tema, así que hay que dárselo. Sin esto queda un
  // color fijo que desaparece en modo oscuro.
  const theme = useTheme();

  const chartData = useMemo(() => {
    const truncate2 = (v: number) => Math.trunc(v * 100) / 100;
    return data.map((s) => ({
      name: s.name,
      value: Number(s.share.toFixed(2)),
      displayValue: `${truncate2(s.share * 100).toFixed(2)}%`,
      type: s.type,
      publishers: s.publishers,
    }));
  }, [data]);

  if (chartData.length === 0) return null;

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || payload.length === 0) return null;
    const d = payload[0].payload;
    const typeLabel =
      d.type === "author"
        ? "Autor"
        : d.type === "publisher"
        ? "Editorial"
        : "";
    const publishers = d.publishers as
      | { name: string; percentage: number }[]
      | undefined;
    return (
      <Box
        sx={{
          backgroundColor: "background.paper",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          p: 1.5,
          boxShadow: 2,
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold">
          {d.name}
        </Typography>
        {typeLabel && (
          <Typography variant="body2" color="text.secondary">
            {typeLabel}
          </Typography>
        )}
        <Typography variant="body2">
          {valueLabel}: {d.displayValue}
        </Typography>
        {d.type === "author" && publishers && publishers.length > 0 && (
          <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: "divider" }}>
            <Typography variant="caption" color="text.secondary">
              Editoriales:
            </Typography>
            {publishers.map((p, i) => (
              <Typography
                key={i}
                variant="caption"
                sx={{ display: "block", pl: 1 }}
              >
                {p.name}: {p.percentage}%
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const renderCustomLabel = ({ displayValue, percent }: any) =>
    percent < 0.05 ? null : displayValue;

  let authorIndex = 0;
  let publisherIndex = 0;

  return (
    <Box sx={{ width: "100%", mt: 2, mb: 2 }}>
      {title && (
        <Typography
          variant="subtitle1"
          fontWeight="medium"
          gutterBottom
          sx={{ textAlign: "center" }}
        >
          {title}
        </Typography>
      )}
      {subtitle && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1, textAlign: "center" }}
        >
          {subtitle}
        </Typography>
      )}
      <ResponsiveContainer width="100%" height={350}>
        <PieChart margin={{ top: 20, right: 50, bottom: 20, left: 60 }}>
          <Pie
            data={chartData}
            cx="45%"
            cy="50%"
            labelLine={false}
            label={renderCustomLabel}
            outerRadius={100}
            fill="#8884d8"
            dataKey="value"
          >
            {chartData.map((entry, index) => {
              let color: string;
              if (entry.type === "author") {
                color = AUTHOR_COLORS[authorIndex++ % AUTHOR_COLORS.length];
              } else if (entry.type === "publisher") {
                color =
                  PUBLISHER_COLORS[publisherIndex++ % PUBLISHER_COLORS.length];
              } else {
                color = UNASSIGNED_COLOR;
              }
              return <Cell key={`cell-${index}`} fill={color} />;
            })}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="bottom"
            wrapperStyle={{ paddingTop: 10 }}
            formatter={(value: string) => (
              <span
                style={{
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.caption.fontSize as string,
                }}
              >
                {value}
              </span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};
